/**
 * Shared utilities for share operations
 */

export type ShareStatus = 'active' | 'revoked' | 'consumed' | 'expired'

export interface ShareStatusInput {
  revoked_at: number | null
  consumed_at: number | null
  expires_at: number
}

/**
 * Determine the status of a share based on its timestamps
 */
export function getShareStatus(share: ShareStatusInput): ShareStatus {
  if (share.revoked_at) return 'revoked'
  if (share.consumed_at) return 'consumed'
  if (share.expires_at < Date.now()) return 'expired'
  return 'active'
}

export interface ShareStatusFields {
  revokedAt: number | null
  consumedAt: number | null
  expiresAt: number
}

/**
 * Determine the status of a share from camelCase UI fields
 */
export function getShareStatusFromFields(share: ShareStatusFields): ShareStatus {
  if (share.revokedAt) return 'revoked'
  if (share.consumedAt) return 'consumed'
  if (share.expiresAt < Date.now()) return 'expired'
  return 'active'
}

export interface ShareStatusBadge {
  label: string
  colorScheme: string
}

const STATUS_BADGES: Record<ShareStatus, ShareStatusBadge> = {
  active: { label: 'Active', colorScheme: 'green' },
  expired: { label: 'Expired', colorScheme: 'gray' },
  consumed: { label: 'Consumed', colorScheme: 'orange' },
  revoked: { label: 'Revoked', colorScheme: 'red' },
}

/**
 * Get display badge info (label + color) for a share status
 */
export function getShareStatusBadge(status: ShareStatus): ShareStatusBadge {
  return STATUS_BADGES[status]
}

/**
 * Check if a share is expired
 */
export function isExpired(expiresAt: number): boolean {
  return expiresAt < Date.now()
}

/**
 * Check if a share is accessible (not revoked, consumed, or expired)
 */
export function isAccessible(share: ShareStatusInput): boolean {
  return getShareStatus(share) === 'active'
}

/**
 * Derive a display name for a share (filename or truncated ID).
 */
export function getShareDisplayName(share: { id: string; filename: string | null }): string {
  return share.filename || `${share.id.slice(0, 8)}.md`
}

/**
 * Check whether a share is encrypted (cipher !== 'NONE').
 */
export function isShareEncrypted(share: { cipher: string }): boolean {
  return share.cipher !== 'NONE'
}
